import { Modal } from "@risalabs_frontend_org/oasis-ui-kit";
import { ModalId } from "../../../enums/modalId";
import OutlineWarningIcon from "../../../svg/outline-warning-icon";

/**
 * Props for the PageLeaveConfirmationModal component.
 */
interface PageLeaveConfirmationModalProps {
  /**
   * Callback triggered when the user clicks "Save & Proceed".
   * Should handle saving changes before allowing navigation.
   */
  onSaveChanges: () => void;

  /**
   * Callback triggered when the user clicks "Proceed without saving".
   * Should handle discarding changes and allowing navigation.
   */
  onDiscard: () => void;

  /**
   * Whether the save button should be disabled.
   * Typically true when there are no changes, saving is in progress, or there are validation errors.
   */
  saveDisabled: boolean;
}

/**
 * A confirmation modal displayed when a user attempts to navigate away from a page
 * with unsaved changes in the CPT Table.
 *
 * @description
 * This modal is used in conjunction with React Router's `useBlocker` hook to intercept
 * navigation when there are unsaved changes. It provides the user with two options:
 * - **Save & Proceed**: Save changes before navigating away
 * - **Proceed without saving**: Discard changes and navigate away
 *
 * @example
 * ```tsx
 * {blocker.state === "blocked" && (
 *   <PageLeaveConfirmationModal
 *     onSaveChanges={handleSaveChanges}
 *     onDiscard={handleDiscardChanges}
 *     saveDisabled={!hasChanges || isSaving}
 *   />
 * )}
 * ```
 *
 * @param props - The component props
 * @returns A Modal component with save/discard options
 */
const PageLeaveConfirmationModal = ({
  onSaveChanges,
  onDiscard,
  saveDisabled,
}: PageLeaveConfirmationModalProps) => {
  return (
    <Modal
      dialogId={ModalId.PAGE_LEAVE_CONFIRMATION_MODAL}
      title="CPT Table Changes"
      onCancel={onDiscard}
      onSave={onSaveChanges}
      saveButtonText="Save & Proceed"
      cancelText="Proceed without saving"
      disableOnCloseFunc={true}
      disableSave={saveDisabled}
      disableCloseOnOutsideClick={true}
      hideCloseButton={true}
    >
      <div className="flex flex-col items-center justify-center gap-4">
        <div className="warning-icon flex h-14 w-14 items-center justify-center rounded-full bg-primaryGray-16">
          <OutlineWarningIcon />
        </div>
        <div className="warning-message text-center text-body font-normal text-primaryGray-1">
          <p>You've unsaved changes in the CPT Table.</p>
          <p> How do you want to Proceed?</p>
        </div>
      </div>
    </Modal>
  );
};

export default PageLeaveConfirmationModal;
